/* eslint-disable jsx-a11y/control-has-associated-label */
import * as yup from 'yup';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { ICourtInputModel, IDistrictInputModel } from 'inputModels';
import { ICourtViewModel, IDistrictViewModel } from 'viewModels';
import ErrorIcon from '@mui/icons-material/Error';
import { Button, DistrictList, Input, NavHeader } from '../../components';
import { MenuOptionLeftCompany } from '../../components/MenuOptionLeft/Company';
import { Body, Container, Content } from './styles';
import { useCourts } from '../../context/courts';
import { CompanyController } from '../../controller/CompanyController';
import { CourtList } from '../../components/CourtList';
import { useDistricts } from '../../context/districts';
import { BRAZILIAN_STATES } from '../../constants';
import { useAlert } from '../../hooks/useAlert';
import { DistrictController } from '../../controller/DistrictController';
import { DropListStatesBrazillian } from '../../components/DropListStatesBrazillian';
import TitlePage from '../../components/TitlePage';
import JoyrideTour from '../../components/JoyrideTour';
import { TutorialType } from '../../types/enums';
import { useAuth } from '../../context';

const defaultValues = {
  districtState: '',
  districtName: '',
  courtName: '',
};

const schema = yup.object({
  districtState: yup.string().required('O estado é obrigatório'),
  districtName: yup.string().required('A comarca é obrigatória'),
  courtName: yup.string().required('Nome da vara ou juizado é obrigatório'),
});

export function Courts() {
  const { user } = useAuth();
  const { courts, setCourts, districtSelectedId, setDistrictSelectedId, setLastAddedCourtId } =
    useCourts();
  const { districts, setDistricts } = useDistricts();
  const alert = useAlert();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  });

  const [districtState, districtName, courtName] = useWatch({
    control,
    name: ['districtState', 'districtName', 'courtName'],
  });
  const [runningTutorial, setRunningTutorial] = useState(false);

  const steps = [
    {
      target: '#start-item',
      content:
        'As varas e juizados referem-se a uma divisão ou departamento dentro do sistema judiciário que lida com casos específicos. Cada vara ou juizado tem sua jurisdição sobre um tipo particular de caso ou assunto legal, a partir desse menu é possível adicionar ou visualizar as varas e juizados já cadastradas.',
      disableBeacon: true,
    },
    {
      target: '#district-state',
      content: 'Primeiramente você irá selecionar o estado da comarca.',
    },
    {
      target: '#district-name',
      content:
        'Após selecionar o estado, você irá preencher o campo comarca com o nome da comarca que deseja adicionar uma vara ou juizado. Caso a comarca já exista, basta selecioná-la na lista que será exibida abaixo do campo.',
    },
    {
      target: '#court-name',
      content:
        'Por fim, você irá preencher o campo vara ou juizado com o nome da vara ou juizado que deseja adicionar. Caso a vara ou juizado já exista, ela irá ser exibida abaixo do campo.',
    },
    {
      target: '#add-court',
      content: 'Clique no botão adicionar para cadastrar a vara ou juizado na comarca selecionada.',
    },
  ];
  const handleAddDistrictAndCourts = async (data: any) => {
    if (districtSelectedId === '') {
      await DistrictController.addDistrict({
        name: data.districtName,
        uf: BRAZILIAN_STATES.find((s) => s.ufCode === Number(data.districtState))?.name || '',
        ufCode: Number(data.districtState),
      } as IDistrictInputModel)
        .then(async (newDistrictId: string) => {
          setDistrictSelectedId(newDistrictId);
          await DistrictController.getDistrictById(newDistrictId)
            .then((district: IDistrictViewModel) => {
              setDistricts([...districts, district]);
              CompanyController.addCourt({
                name: data.courtName,
                districtId: newDistrictId,
              } as ICourtInputModel)
                .then((newCourtId: string) => {
                  setCourts([
                    ...courts,
                    { id: newCourtId, name: data.courtName } as ICourtViewModel,
                  ]);
                  setLastAddedCourtId(newCourtId);
                })
                .catch((error) => {
                  const errorType = error.response.data.Message;
                  alert.show({
                    message:
                      errorType === 'ALREADY_REGISTERED'
                        ? 'Você está tentando adicionar uma vara ou juizado já existente'
                        : 'Houve um erro ao adicionar uma nova vara ou juizado',
                    type: 'error',
                  });
                });
            })
            .catch(() => {
              alert.show({
                message: 'Houve um erro ao buscar a nova comarca adicionada',
                type: 'error',
              });
            });
        })
        .catch((error) => {
          const errorType = error.response.data.Message;
          alert.show({
            message:
              errorType === 'ALREADY_REGISTERED'
                ? 'Você está tentando adicionar uma comarca já existente'
                : 'Houve um erro ao adicionar uma nova comarca',
            type: 'error',
          });
        });
    } else {
      CompanyController.addCourt({
        name: data.courtName,
        districtId: districtSelectedId,
      } as ICourtInputModel)
        .then((newCourtId) => {
          setCourts([...courts, { id: newCourtId, name: data.courtName } as ICourtViewModel]);
          setLastAddedCourtId(newCourtId);
        })
        .catch((error) => {
          const errorType = error.response.data.Message;
          alert.show({
            message:
              errorType === 'ALREADY_REGISTERED'
                ? 'Você está tentando adicionar uma comarca já existente'
                : 'Houve um erro ao adicionar uma nova comarca',
            type: 'error',
          });
        });
    }
  };

  useEffect(() => {
    const districtExists = () => {
      const courtDistrictName = districts.find(
        (district) => district.id === districtSelectedId,
      )?.name;
      return courtDistrictName === districtName;
    };

    if (!districtExists()) {
      setCourts([]);
      setDistrictSelectedId('');
    }
  }, [districtName, courts, setCourts, setDistrictSelectedId, districts, districtSelectedId]);

  const handleStateChange = async (ufCode: number) => {
    const alreadyFound = districts.some((district) => district.ufCode === ufCode);
    if (alreadyFound) return;

    const districtsByUfCode = await DistrictController.getDistrictsByUfCode(ufCode);
    setDistricts([...districtsByUfCode]);
  };
  useEffect(() => {
    if (!user?.tutorialProgress?.some((p) => p === TutorialType.COURT)) {
      setRunningTutorial(true);
    }
  }, [user.tutorialProgress]);

  return (
    <>
      <NavHeader typeHeader="company" />
      <JoyrideTour
        type={TutorialType.COURT}
        steps={steps}
        run={runningTutorial}
        onCallbackFinished={() => setRunningTutorial(false)}
      />
      <Body>
        <MenuOptionLeftCompany />
        <Container>
          <TitlePage
            title="Comarcas, varas e juizados"
            onRequestOpen={() => setRunningTutorial(true)}
            id="start-item"
          />
          <Content>
            <div className="statesContainer">
              <Controller
                control={control}
                name="districtState"
                render={({ field: { onChange } }) => (
                  <DropListStatesBrazillian
                    placeholderTitle="Estado"
                    elementId="district-state"
                    onChange={(ufCode) => {
                      onChange(ufCode);
                      handleStateChange(Number(ufCode));
                    }}
                  />
                )}
              />
              {errors.districtState?.message && (
                <span className="fieldError">
                  <ErrorIcon fontSize="inherit" /> {errors.districtState?.message}
                </span>
              )}
            </div>
            <div className="districtContainer">
              <Input
                name="districtName"
                control={control}
                placeholder="Comarca"
                variant="standard"
                errorText={errors.districtName?.message}
                id="district-name"
              />
              <DistrictList
                name="districtName"
                control={control}
                districtState={Number(districtState)}
                filterValue={districtName}
              />
            </div>

            <div className="courtContainer">
              <Input
                name="courtName"
                control={control}
                placeholder="Nome da vara ou juizado"
                id="court-name"
                variant="standard"
                errorText={errors.courtName?.message}
              />
              <CourtList
                name="courtName"
                control={control}
                districtId={districtSelectedId}
                filterValue={courtName}
              />
            </div>
            <div className="addCourt">
              <Button
                text="Adicionar"
                id="add-court"
                dimensions={{ width: '13rem', height: '2.25rem' }}
                onClick={handleSubmit(handleAddDistrictAndCourts)}
              />
            </div>
          </Content>
        </Container>
      </Body>
    </>
  );
}
